import { useState, useEffect, useRef, useCallback } from 'react';
import { publicCitiesApi } from '@/services/api';

interface CityOption {
  name: string;
  state: string;
}

interface CityAutocompleteProps {
  value: string;
  onChange: (city: string) => void;
  onStateChange?: (state: string) => void;
  state?: string;
  placeholder?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  minChars?: number;
}

/**
 * Reusable database-backed city autocomplete.
 *
 * Fires a debounced call to GET /api/cities/suggest after the user types
 * 2+ letters, shows a dropdown of matches, and on selection auto-fills the
 * parent state field via `onStateChange`.
 */
export default function CityAutocomplete({
  value,
  onChange,
  onStateChange,
  state,
  placeholder = 'Start typing a city...',
  name,
  className = '',
  disabled = false,
  minChars = 2,
}: CityAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [options, setOptions] = useState<CityOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setQuery(value), [value]);

  const runSearch = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed || trimmed.length < minChars) {
      setOptions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    publicCitiesApi
      .suggest(trimmed, state)
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        setOptions(arr);
        setOpen(arr.length > 0);
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [state, minChars]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    setOpen(false);
    setActive(-1);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(val), 300);
  };

  const selectOption = useCallback((opt: CityOption) => {
    onChange(opt.name);
    setQuery(opt.name);
    if (onStateChange) onStateChange(opt.state);
    setOpen(false);
    setActive(-1);
  }, [onChange, onStateChange]);

  // Close when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => { if (options.length > 0) setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, options.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter' && open && active >= 0 && options[active]) {
            e.preventDefault();
            selectOption(options[active]);
          }
        }}
        placeholder={placeholder}
        name={name}
        disabled={disabled}
        autoComplete="off"
        className={`w-full bg-background-100 border border-background-300 text-foreground-950 text-sm rounded-xl pl-4 pr-10 py-3 focus:outline-none focus:border-primary-500 placeholder:text-foreground-400 transition-colors ${className}`}
      />
      {loading && (
        <i className="ri-loader-4-line animate-spin absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
      )}

      {open && options.length > 0 && (
        <ul className="absolute z-30 mt-1.5 w-full bg-white border border-background-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {options.map((opt, i) => (
            <li key={`${opt.name}-${opt.state}-${i}`}>
              <button
                type="button"
                onClick={() => selectOption(opt)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3.5 py-2.5 text-sm cursor-pointer flex items-center gap-2.5 transition-colors ${i === active ? 'bg-background-100' : 'bg-transparent'}`}
              >
                <i className="ri-map-pin-line text-foreground-400 flex-shrink-0" />
                <span className="text-foreground-950">{opt.name}</span>
                <span className="ml-auto text-[11px] text-foreground-400 whitespace-nowrap">{opt.state}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}